import { useMemo } from 'react';
import { useTheme } from './useTheme';

/**
 * Convert hex color to RGB
 */
function hexToRgb(hex) {
    // Remove # if present
    hex = hex.replace(/^#/, '');

    // Parse hex values
    const r = Number.parseInt(hex.substring(0, 2), 16);
    const g = Number.parseInt(hex.substring(2, 4), 16);
    const b = Number.parseInt(hex.substring(4, 6), 16);

    return [r, g, b];
}

/**
 * Calculate relative luminance of a color
 * Formula: https://www.w3.org/TR/WCAG20/#relativeluminancedef
 */
function calculateLuminance(hexColor) {
    const [r, g, b] = hexToRgb(hexColor);

    // Convert to 0-1 range and apply gamma correction
    const [rs, gs, bs] = [r, g, b].map((val) => {
        val = val / 255;
        return val <= 0.03928 ? val / 12.92 : ((val + 0.055) / 1.055) ** 2.4;
    });

    // Calculate luminance
    return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
}

/**
 * Hook para obter a cor adaptativa dos icones
 * Retorna cor clara (#ffffff) para fundos escuros e cor escura (#000000) para fundos claros
 */
export function useIconColor() {
    const { predefinedThemes, theme } = useTheme();

    const iconColor = useMemo(() => {
        // Se e um tema predefinido, usar o icon_color dele
        if (predefinedThemes[theme]?.icon_color) {
            return predefinedThemes[theme].icon_color;
        }

        // Fallback: calcular baseado na luminosidade do background
        try {
            const bgColor =
                getComputedStyle(document.documentElement).getPropertyValue('--color-primary').trim() || '#000000';

            const luminance = calculateLuminance(bgColor);

            // Se fundo escuro (luminance < 0.5), usar icones claros
            // Se fundo claro (luminance >= 0.5), usar icones escuros
            return luminance < 0.5 ? '#ffffff' : '#000000';
        } catch (_error) {
            return '#ffffff';
        }
    }, [predefinedThemes, theme]);

    return iconColor;
}

export default useIconColor;
